import { useAuthStore } from '@/stores/authStore'
import { useLocation, useParams } from '@tanstack/react-router'

interface RouteConfig {
	path: string
	routePattern: string
	storageKey: string
	paramName?: 'status' | 'type' | 'module'
}

const ROUTE_CONFIGS: RouteConfig[] = [
	{
		path: '/device-management',
		routePattern: '/orgs/$orgId/projects/$tenantId/device-management/$status',
		storageKey: 'device-management-filters',
		paramName: 'status',
	},
	{
		path: '/data/dashboards',
		routePattern: '/orgs/$orgId/projects/$tenantId/data/dashboards/$type',
		storageKey: 'dashboards-filters',
		paramName: 'type',
	},
	{
		path: '/actions/pushed',
		routePattern: '/orgs/$orgId/projects/$tenantId/actions/pushed/$status',
		storageKey: 'actions-filters',
		paramName: 'status',
	},
	{
		path: '/data/dbc-parsers',
		routePattern: '/orgs/$orgId/projects/$tenantId/data/dbc-parsers/$type',
		storageKey: 'dbc-parsers-filters',
		paramName: 'type',
	},
	{
		path: '/inventory/firmware',
		routePattern: '/orgs/$orgId/projects/$tenantId/inventory/firmware/$status',
		storageKey: 'firmware-filters',
		paramName: 'status',
	},
	{
		path: '/inventory/configs',
		routePattern: '/orgs/$orgId/projects/$tenantId/inventory/configs/$status',
		storageKey: 'configs-filters',
		paramName: 'status',
	},
	{
		path: '/actions/ota-campaigns',
		routePattern: '/orgs/$orgId/projects/$tenantId/actions/ota-campaigns/$status',
		storageKey: 'ota-campaigns-filters',
		paramName: 'status',
	},
	{
		path: '/data/pushers',
		routePattern: '/orgs/$orgId/projects/$tenantId/data/pushers/$type',
		storageKey: 'pushers-filters',
		paramName: 'type',
	},
	{
		path: '/sessions/dashboard',
		routePattern: '/orgs/$orgId/projects/$tenantId/sessions/dashboard/$type',
		storageKey: 'sessions-dashboard-filters',
		paramName: 'type',
	},
	{
		path: '/settings',
		routePattern: '/orgs/$orgId/projects/$tenantId/settings',
		storageKey: 'settings-filters',
	},
	{
		path: '/inventory',
		routePattern: '/orgs/$orgId/inventory/$status',
		storageKey: 'inventory-filters',
		paramName: 'status',
	},
	{
		path: '/meters',
		routePattern: '/orgs/$orgId/meters/$module',
		storageKey: 'meters-filters',
		paramName: 'module',
	},
	{
		path: '/operations',
		routePattern: '/orgs/$orgId/operations/$type',
		storageKey: 'operations-filters',
		paramName: 'type',
	},
]

function findRouteConfig(normalizedPath: string): RouteConfig | undefined {
	return ROUTE_CONFIGS.find(config => normalizedPath.includes(config.path))
}

export function useRouteContext() {
	const location = useLocation()
	const params = useParams({ strict: false })
	const paramsRecord = params as Record<string, string>

	return {
		pathname: location.pathname,
		search: location.search,
		params: paramsRecord,
		tenantId: paramsRecord?.tenantId,
		orgId: paramsRecord?.orgId,
	}
}

export function normalizePath(path: string): string {
	const parts = path.split('/').filter(Boolean)
	if (parts[0] === 'orgs' && parts.length > 4 && parts[1] && parts[2] === 'projects' && parts[3]) {
		return `/${parts.slice(4).join('/')}`
	}

	if (parts[0] === 'orgs' && parts.length > 2 && parts[1]) {
		return `/${parts.slice(2).join('/')}`
	}

	return parts.join('/')
}

export function createProjectPath(basePath: string, tenantId: string, orgId: string): string {
	if (!orgId) {
		throw new Error('organization ID is required to create project path')
	}
	return `/orgs/${orgId}/projects/${tenantId}${basePath}`
}

export function getRouteInfo(
	normalizedPath: string,
	tenantId: string | undefined,
	lastPart: string,
	orgId: string | undefined,
) {
	const routeConfig = findRouteConfig(normalizedPath)

	if (!routeConfig || !orgId) {
		return { linkTo: '', linkParams: {}, hasParams: false }
	}

	const linkParams: Record<string, string> = { orgId }
	if (tenantId) {
		linkParams.tenantId = tenantId
	}
	if (routeConfig.paramName) {
		linkParams[routeConfig.paramName] = lastPart
	}

	return {
		linkTo: routeConfig.routePattern,
		linkParams,
		hasParams: !!routeConfig.paramName,
	}
}

export function getRoutePatternFromPath(pathname: string, tenantId: string, orgId: string): string {
	if (!orgId) {
		throw new Error('orgId is required to extract route pattern')
	}
	const normalizedPath = pathname.replace(`/orgs/${orgId}/projects/${tenantId}`, '')
	const routeConfig = findRouteConfig(normalizedPath)
	return routeConfig?.routePattern || pathname
}

export function createNavigationOptions(
	currentPath: string,
	orgId: string,
	tenantId: string,
	search: unknown,
	replace = false,
) {
	if (!orgId) {
		throw new Error('orgId is required to create navigation options')
	}
	if (currentPath.startsWith('/orgs/')) {
		return {
			to: currentPath,
			search,
			replace,
		}
	}

	const fullPath = createProjectPath(currentPath, tenantId, orgId)
	return {
		to: fullPath,
		search,
		replace,
	}
}

export function useCurrentRouteParams() {
	const params = useParams({ strict: false })
	return params as Record<string, string>
}

export function resolveOrganizationId(): string {
	const orgId = getOrganizationIdFromURL() || useAuthStore.getState().currentOrganizationId
	if (!orgId) {
		throw new Error('Organization ID is required')
	}
	return orgId
}

export function resolveOrganizationIdOrEmpty(): string {
	return getOrganizationIdFromURL() || useAuthStore.getState().currentOrganizationId || ''
}

export function getOrganizationIdFromURL(url?: string): string | null {
	try {
		const currentURL = new URL(url || window.location.href)
		const pathSegments = currentURL.pathname.split('/').filter(Boolean)
		const orgIndex = pathSegments.indexOf('orgs')
		if (orgIndex !== -1 && pathSegments[orgIndex + 1]) {
			const nextSegment = pathSegments[orgIndex + 1]
			if (nextSegment && nextSegment !== 'projects') {
				return nextSegment
			}
		}
		return null
	} catch (error) {
		console.error(`Error parsing URL for organization: ${error}`)
		return null
	}
}

export function useCurrentOrganizationId(): string | null {
	const params = useParams({ strict: false })
	const orgIdFromState = useAuthStore(state => state.currentOrganizationId)
	const orgIdFromUrl = (params as Record<string, string>)?.orgId
	if (orgIdFromUrl) {
		return orgIdFromUrl
	}
	return orgIdFromState
}

export function isProjectLevelRoute(pathname: string): boolean {
	return /^\/orgs\/[^/]+\/projects\/[^/]+(\/|$)/.test(pathname)
}

export function isOrganizationLevelRoute(pathname: string): boolean {
	if (isProjectLevelRoute(pathname)) {
		return false
	}
	return /^\/orgs\/[^/]+\/(inventory|meters|operations|billing|audits|settings)(\/|$)/.test(pathname)
}

export const DEFAULT_PROJECT_LANDING_SUFFIX = '/device-management/all'
export function getDefaultRouteForUser(tenantId: string, orgId?: string): string | null {
	if (!orgId) {
		return null
	}
	return `/orgs/${orgId}/projects/${tenantId}${DEFAULT_PROJECT_LANDING_SUFFIX}`
}
